/* import __COLOCATED_TEMPLATE__ from './cx-distribution.hbs'; */
/* RESPONSIBLE TEAM: team-ai-insights */
import Component from '@glimmer/component';
import type { Options, SeriesOptionsType, AxisLabelsFormatterContextObject } from 'highcharts';
import { interpolatePercentileRank } from 'embercom/lib/reporting/cx-benchmarks/cx-benchmark-service';
import {
  buildDensityCurve,
  getValueAtPercentile,
} from 'embercom/lib/reporting/cx-benchmarks/density-curve';
import type { CxBenchmarkGroup } from './types';

interface Signature {
  Args: {
    benchmarkGroup: CxBenchmarkGroup | undefined;
    industryLabel: string;
    appCxScore: number | undefined;
  };
}

interface TickEntry {
  pos: number;
  label: string;
}

export default class CxDistribution extends Component<Signature> {
  get appCxScore(): number {
    return this.args.appCxScore ?? 0;
  }

  get hasAppCxScore(): boolean {
    return this.args.appCxScore !== undefined;
  }

  get appCxScorePercent(): string {
    return `${(this.appCxScore * 100).toFixed(1)}%`;
  }

  get percentileRank(): number {
    let group = this.args.benchmarkGroup;
    if (!group) {
      return 0;
    }
    return Math.round(interpolatePercentileRank(group, this.appCxScore));
  }

  get hasBenchmarkData(): boolean {
    return this.args.benchmarkGroup !== undefined && this.hasAppCxScore;
  }

  get curveData(): [number, number][] {
    let group = this.args.benchmarkGroup;
    if (!group) {
      return [];
    }
    return buildDensityCurve(group.percentiles);
  }

  private get tickMap(): TickEntry[] {
    let group = this.args.benchmarkGroup;
    if (!group) {
      return [];
    }
    let percentiles = group.percentiles;
    let curve = this.curveData;
    if (curve.length === 0) {
      return [];
    }

    let minX = curve[0][0];
    let maxX = curve[curve.length - 1][0];

    return [
      { pos: minX, label: '0th' },
      { pos: getValueAtPercentile(percentiles, 0.25), label: '25th' },
      { pos: getValueAtPercentile(percentiles, 0.5), label: '50th' },
      { pos: getValueAtPercentile(percentiles, 0.75), label: '75th' },
      { pos: maxX, label: '100th' },
    ];
  }

  get highchartsSeries(): SeriesOptionsType[] {
    return [
      {
        type: 'areaspline' as const,
        data: this.curveData,
        fillColor: 'var(--neutral-container-emphasis)',
        lineWidth: 0,
        lineColor: 'transparent',
        marker: { enabled: false },
        enableMouseTracking: false,
        threshold: 0,
      },
    ];
  }

  get highchartsOptions(): Options {
    let ticks = this.tickMap;
    let tickPositions = ticks.map((t) => t.pos);
    let percentileRank = this.percentileRank;
    let appCxScore = this.appCxScore;

    return {
      chart: {
        type: 'areaspline',
        height: 300,
        spacing: [50, 20, 25, 50],
        backgroundColor: 'transparent',
        style: {
          fontFamily: 'inherit',
        },
      },

      title: { text: undefined },
      legend: { enabled: false },
      credits: { enabled: false },
      tooltip: { enabled: false },

      yAxis: {
        visible: false,
        gridLineWidth: 0,
      },

      xAxis: {
        tickPositions,
        labels: {
          useHTML: true,
          formatter(this: AxisLabelsFormatterContextObject) {
            // Find the closest tick entry by position
            let closest: TickEntry | null = null;
            let minDist = Infinity;
            for (let entry of ticks) {
              let dist = Math.abs(Number(this.value) - entry.pos);
              if (dist < minDist) {
                minDist = dist;
                closest = entry;
              }
            }

            if (!closest) {
              return '';
            }

            if (closest.label === '0th') {
              return '<span style="color:var(--text-disabled);font-size:13px;">0th</span><br><span style="color:var(--text-disabled);font-size:12px;">Percentile</span>';
            }
            return `<span style="color:var(--text-disabled);font-size:13px;">${closest.label}</span>`;
          },
        },
        lineColor: 'transparent',
        tickLength: 0,
        gridLineWidth: 0,
        plotLines: [
          // Percentile divider lines — background-colored to "cut" through the fill
          ...ticks.slice(1).map((t) => ({
            value: t.pos,
            color: 'var(--base-module)',
            width: 3,
            zIndex: 4,
          })),
          // "You are here" marker
          {
            value: appCxScore,
            color: 'var(--text-default)',
            width: 1.5,
            zIndex: 5,
            label: {
              useHTML: true,
              text: `<span style="display:inline-block;background:var(--neutral-fill);color:var(--text-on-neutral);border-radius:12px;padding:2px 8px;font-size:13px;font-weight:600;line-height:1.3;">${percentileRank}th</span>`,
              rotation: 0,
              verticalAlign: 'top',
              textAlign: 'center',
              y: -14,
              x: 0,
            },
          },
        ],
      },
    };
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Reporting::Bespoke::FinPerformance::CxBenchmarkPanel::CxDistribution': typeof CxDistribution;
    'reporting/bespoke/fin-performance/cx-benchmark-panel/cx-distribution': typeof CxDistribution;
  }
}
